import { Nullable } from '../types';
import { ConnectedAccountStatus } from '../types/banking';
import { AipriseDecision, Company, CompanyOnboardingStep, OnboardingStage } from '../types/companyUser';

// Single source of truth for whether a company is waiting for its verification to be reviewed:
// onboarding is submitted but the banking account has not been enabled yet.
// Used by both the pending banner (ActivationBanner) and the compliance upsell (ComplianceBanner).
export const isVerificationPending = (company?: Nullable<Company>): boolean =>
  company?.onboardingStep === CompanyOnboardingStep.Completed &&
  !company?.productStatus?.readyForBankingAccountCreation &&
  !company?.productStatus?.readyForBanking;

export const isGlobalAccountActive = (company?: Nullable<Company>): boolean =>
  !!company?.productStatus?.readyForBanking && !!company?.productStatus?.hasStartedBanking;

// A company is activating its Global Account when it finished the onboarding or Marco already
// enabled its banking account, and it has not started banking yet. While activating, the home
// shows the onboarding tracker and every other product request is locked.
export const isActivatingGlobalAccount = (company?: Nullable<Company>): boolean =>
  !!company &&
  !isGlobalAccountActive(company) &&
  (company.onboardingStep === CompanyOnboardingStep.Completed ||
    !!company.productStatus?.readyForBankingAccountCreation ||
    !!company.productStatus?.readyForBanking);

export const getOnboardingStage = (
  company: Company,
  bankingStatus?: ConnectedAccountStatus
): OnboardingStage => {
  const { readyForBanking, hasStartedBanking, readyForBankingAccountCreation } = company.productStatus;
  if (readyForBanking && hasStartedBanking) return OnboardingStage.Active;
  if (readyForBanking) return OnboardingStage.Terms;
  if (bankingStatus === ConnectedAccountStatus.ACTION_REQUIRED) return OnboardingStage.ActionRequired;
  if (bankingStatus && bankingStatus !== ConnectedAccountStatus.CREATED) return OnboardingStage.Verification;
  if (readyForBankingAccountCreation) return OnboardingStage.Application;
  if (company.aipriseValidation?.decision === AipriseDecision.ManualReviewRequired) {
    return OnboardingStage.ManualReview;
  }
  return OnboardingStage.Screening;
};
